import { Table, Form, Button, Dropdown } from 'react-bootstrap';
import { useGetOtherExpensesQuery } from '../../slices/otherExpensesApiSlice';
import { useState, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const OtherExpenseInvoices = () => {
  const { data: otherExpensesData } = useGetOtherExpensesQuery({});

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [detailsInput, setDetailsInput] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [sortMode, setSortMode] = useState('date'); // date | month | category
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // CATEGORY LIST
  const categories = useMemo(() => {
    if (!otherExpensesData?.otherExpenses) return [];
    return [...new Set(otherExpensesData.otherExpenses.map(e => e.category).filter(Boolean))];
  }, [otherExpensesData]);

  const toggleCategory = (cat) => {
    setSelectedCategories(prev =>
      prev.includes(cat)
        ? prev.filter(c => c !== cat)
        : [...prev, cat]
    );
  };

  // FILTERING
  const filteredOtherExpenses = useMemo(() => {
    if (!otherExpensesData?.otherExpenses) return [];

    return otherExpensesData.otherExpenses.filter(exp => {
      const expDate = new Date(exp.date).toISOString().split('T')[0];

      const matchesDate =
        (!startDate || expDate >= startDate) &&
        (!endDate || expDate <= endDate);

      const matchesCategory =
        selectedCategories.length === 0 ||
        selectedCategories.includes(exp.category);

      const matchesDetails =
        detailsInput === '' ||
        exp.details?.toLowerCase().includes(detailsInput.toLowerCase());

      return matchesDate && matchesCategory && matchesDetails;
    });
  }, [otherExpensesData, startDate, endDate, selectedCategories, detailsInput]);

  // UNIVERSAL SORT ENGINE
  const universalSort = (arr, keyExtractor) => {
    return [...arr].sort((a, b) => {
      const keyA = keyExtractor(a);
      const keyB = keyExtractor(b);

      if (keyA < keyB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (keyA > keyB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // DAILY TOTALS
  const dailyTotals = useMemo(() => {
    const map = {};

    filteredOtherExpenses.forEach(exp => {
      const d = new Date(exp.date).toISOString().split('T')[0];
      map[d] = (map[d] || 0) + Number(exp.amount || 0);
    });

    const arr = Object.entries(map).map(([date, total]) => ({
      date,
      total,
      month: date.slice(0, 7)
    }));

    return universalSort(arr, item => {
      if (sortMode === 'month') return item.month;
      return item.date;
    });
  }, [filteredOtherExpenses, sortMode, sortConfig]);

  // DAILY CATEGORY TOTALS
  const dailyCategoryTotals = useMemo(() => {
    const map = {};

    filteredOtherExpenses.forEach(exp => {
      const date = new Date(exp.date).toISOString().split('T')[0];
      const cat = exp.category || 'Uncategorized';
      const amount = Number(exp.amount || 0);

      if (!map[date]) map[date] = {};
      if (!map[date][cat]) map[date][cat] = 0;

      map[date][cat] += amount;
    });

    const arr = Object.entries(map).map(([date, categories]) => ({
      date,
      month: date.slice(0, 7),
      categories: Object.entries(categories).map(([cat, total]) => ({
        cat,
        total
      }))
    }));

    return universalSort(arr, item => {
      if (sortMode === 'month') return item.month;
      if (sortMode === 'category') return item.categories[0]?.cat || '';
      return item.date;
    });
  }, [filteredOtherExpenses, sortMode, sortConfig]);

  // MONTHLY TOTALS
  const monthlyTotals = useMemo(() => {
    const map = {};

    filteredOtherExpenses.forEach(exp => {
      const d = new Date(exp.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      map[key] = (map[key] || 0) + Number(exp.amount || 0);
    });

    const arr = Object.entries(map).map(([month, total]) => ({
      month,
      total,
      date: new Date(month + '-01')
    }));

    return universalSort(arr, item => {
      if (sortMode === 'month') return item.month;
      return item.date;
    });
  }, [filteredOtherExpenses, sortMode, sortConfig]);

  // CATEGORY TOTALS
  const categoryTotals = useMemo(() => {
    const map = {};
    const dateMap = {};

    filteredOtherExpenses.forEach(exp => {
      const cat = exp.category || 'Uncategorized';
      const amount = Number(exp.amount || 0);
      const d = new Date(exp.date);

      map[cat] = (map[cat] || 0) + amount;

      if (!dateMap[cat] || d < dateMap[cat]) {
        dateMap[cat] = d;
      }
    });

    const arr = Object.entries(map).map(([cat, total]) => ({
      cat,
      total,
      date: dateMap[cat],
      month: `${dateMap[cat].getFullYear()}-${String(dateMap[cat].getMonth() + 1).padStart(2, '0')}`
    }));

    return universalSort(arr, item => {
      if (sortMode === 'category') return item.cat;
      if (sortMode === 'month') return item.month;
      return item.date;
    });
  }, [filteredOtherExpenses, sortMode, sortConfig]);

  // GROUPED SUMMARY (Category → Note)
  const groupedSummary = useMemo(() => {
    const summary = {};

    filteredOtherExpenses.forEach(exp => {
      const cat = exp.category || 'Uncategorized';
      const note = exp.notes?.trim() || '[Blank]';

      if (!summary[cat]) summary[cat] = {};

      if (!summary[cat][note]) {
        summary[cat][note] = {
          details: exp.details,
          total: Number(exp.amount || 0),
          date: exp.date,
        };
      } else {
        summary[cat][note].total += Number(exp.amount || 0);
      }
    });

    return summary;
  }, [filteredOtherExpenses]);

  // SORT GROUPED SUMMARY
  const sortedSummary = useMemo(() => {
    const sorted = {};

    Object.keys(groupedSummary).forEach(cat => {
      const notes = Object.entries(groupedSummary[cat]);

      notes.sort((a, b) => {
        const entryA = a[1];
        const entryB = b[1];

        if (sortMode === 'category') {
          return sortConfig.direction === 'asc'
            ? cat.localeCompare(cat)
            : cat.localeCompare(cat);
        }

        if (sortMode === 'month') {
          const mA = entryA.date.slice(0, 7);
          const mB = entryB.date.slice(0, 7);
          if (mA < mB) return sortConfig.direction === 'asc' ? -1 : 1;
          if (mA > mB) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }

        const dA = new Date(entryA.date);
        const dB = new Date(entryB.date);

        if (dA < dB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (dA > dB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });

      sorted[cat] = Object.fromEntries(notes);
    });

    return sorted;
  }, [groupedSummary, sortMode, sortConfig]);

  // GRAND TOTAL
  const grandTotal = useMemo(() => {
    let total = 0;
    Object.values(groupedSummary).forEach(notes => {
      Object.values(notes).forEach(entry => {
        total += entry.total;
      });
    });
    return total;
  }, [groupedSummary]);

  // PAGINATION
  const paginatedCategories = useMemo(() => {
    const allRows = [];

    Object.entries(sortedSummary).forEach(([cat, notes]) => {
      allRows.push({ type: 'category', cat });

      let categoryTotal = 0;

      Object.entries(notes).forEach(([note, entry]) => {
        categoryTotal += entry.total;

        allRows.push({
          type: 'row',
          cat,
          note,
          ...entry,
        });
      });

      allRows.push({
        type: 'subtotal',
        cat,
        total: categoryTotal,
      });
    });

    const start = (currentPage - 1) * pageSize;
    return allRows.slice(start, start + pageSize);
  }, [sortedSummary, currentPage, pageSize]);

  const totalPages = useMemo(() => {
    let count = 0;
    Object.values(sortedSummary).forEach(notes => {
      count += 2 + Object.keys(notes).length;
    });
    return Math.ceil(count / pageSize);
  }, [sortedSummary, pageSize]);

  const requestSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // PRINT & EXPORT FUNCTIONS (unchanged)
  const printDailySummary = () => {
    const html = `
      <html>
      <head>
      <title>Daily OtherExpense Summary</title>
      <style>
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #000; padding: 6px; text-align: left; }
      </style>
      </head>
      <body>
      <h2>Daily OtherExpense Summary</h2>
      <table>
      <thead>
      <tr><th>Date</th><th>Total Amount</th></tr>
      </thead>
      <tbody>
      ${dailyTotals
        .map(d => `
        <tr>
        <td>${new Date(d.date).toLocaleDateString()}</td>
        <td>${d.total.toFixed(2)}</td>
        </tr>
        `)
        .join('')}
      </tbody>
      </table>
      </body>
      </html>
    `;
    const win = window.open('', '', 'width=900,height=700');
    win.document.write(html);
    win.document.close();
    win.print();
  };

  const exportDailySummary = () => {
    const rows = dailyTotals.map(d => ({
      Date: d.date,
      Total: d.total,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Daily Summary');

    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer]), 'Daily_OtherExpense_Summary.xlsx');
  };

  const handleExport = () => {
    const rows = [];

    Object.entries(sortedSummary).forEach(([cat, notes]) => {
      let categoryTotal = 0;

      Object.entries(notes).forEach(([note, entry]) => {
        categoryTotal += entry.total;

        rows.push({
          Category: cat,
          Note: note,
          Details: entry.details,
          Date: entry.date ? new Date(entry.date).toLocaleDateString('en-US') : '',
          Total: entry.total,
        });
      });

      rows.push({
        Category: cat,
        Note: 'Subtotal',
        Details: '',
        Date: '',
        Total: categoryTotal,
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Summary');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([excelBuffer]), 'OtherExpense_Summary.xlsx');
  };

  const handlePrint = () => {
    const rows = [];

    Object.entries(sortedSummary).forEach(([cat, notes]) => {
      let categoryTotal = 0;

      rows.push({ type: 'category', cat });

      Object.entries(notes).forEach(([note, entry]) => {
        categoryTotal += entry.total;

        rows.push({
          type: 'row',
          cat,
          note,
          details: entry.details,
          date: entry.date ? new Date(entry.date).toLocaleDateString('en-US') : '',
          total: entry.total.toFixed(2),
        });
      });

      rows.push({
        type: 'subtotal',
        cat,
        total: categoryTotal.toFixed(2),
      });
    });

    const printContent = `
      <html>
      <head>
      <title>OtherExpense Summary</title>
      <style>
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #000; padding: 6px; text-align: left; }
      .category { background: #cce5ff; font-weight: bold; }
      .subtotal { background: #e2e3e5; font-weight: bold; }
      </style>
      </head>
      <body>
      <h2>OtherExpense Summary</h2>
      <h3>Grand Total: ${grandTotal.toFixed(2)}</h3>
      <table>
      <thead>
      <tr>
      <th>Category</th>
      <th>Note</th>
      <th>Details</th>
      <th>Date</th>
      <th>Total</th>
      </tr>
      </thead>
      <tbody>
      ${rows
        .map(row => {
          if (row.type === 'category') {
            return `<tr class="category"><td colspan="5">${row.cat}</td></tr>`;
          }
          if (row.type === 'subtotal') {
            return `<tr class="subtotal"><td colspan="4">Subtotal</td><td>${row.total}</td></tr>`;
          }
          return `
            <tr>
            <td>${row.cat}</td>
            <td>${row.note}</td>
            <td>${row.details}</td>
            <td>${row.date}</td>
            <td>${row.total}</td>
            </tr>
          `;
        })
        .join('')}
      </tbody>
      </table>
      </body>
      </html>
    `;

    const printWindow = window.open('', '', 'width=900,height=700');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const printDailyCategoryTotals = () => {
    const html = `
      <html>
      <head>
      <title>Daily Category Totals</title>
      <style>
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #000; padding: 6px; text-align: left; }
      .date-row { background: #cce5ff; font-weight: bold; }
      </style>
      </head>
      <body>
      <h2>Daily Category Totals</h2>
      <table>
      <thead>
      <tr>
      <th>Date</th>
      <th>Category</th>
      <th>Total Amount</th>
      </tr>
      </thead>
      <tbody>
      ${dailyCategoryTotals
        .map(day => {
          return `
            <tr class="date-row">
            <td colspan="3">${new Date(day.date).toLocaleDateString()}</td>
            </tr>
            ${day.categories
              .map(c => `
                <tr>
                <td></td>
                <td>${c.cat}</td>
                <td>${c.total.toFixed(2)}</td>
                </tr>
              `)
              .join('')}
          `;
        })
        .join('')}
      </tbody>
      </table>
      </body>
      </html>
    `;
    const win = window.open('', '', 'width=900,height=700');
    win.document.write(html);
    win.document.close();
    win.print();
  };

  const exportDailyCategoryTotals = () => {
    const rows = [];

    dailyCategoryTotals.forEach(day => {
      day.categories.forEach(c => {
        rows.push({
          Date: day.date,
          Category: c.cat,
          Total: c.total,
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Daily Category Totals');

    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer]), 'Daily_Category_Totals.xlsx');
  };

return (
  <div className="container mt-4">
    <h1 className="text-center">Invoice Summary</h1>

    <h4 className="text-center mb-3">
      Grand Total: {grandTotal.toFixed(2)}
    </h4>

    {/* FILTERS */}
    <Form className="mb-3 d-flex justify-content-center flex-wrap">
      <Form.Group className="mx-2">
        <Form.Label>Start Date</Form.Label>
        <Form.Control
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mx-2">
        <Form.Label>End Date</Form.Label>
        <Form.Control
          type="date"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
        />
      </Form.Group>

      <Dropdown className="mx-2">
        <Dropdown.Toggle variant="secondary">Select Categories</Dropdown.Toggle>
        <Dropdown.Menu style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {categories.map(cat => (
            <Form.Check
              key={cat}
              type="checkbox"
              label={cat}
              checked={selectedCategories.includes(cat)}
              onChange={() => toggleCategory(cat)}
              className="px-3"
            />
          ))}
        </Dropdown.Menu>
      </Dropdown>

      <Form.Group className="mx-2">
        <Form.Label>Details Filter</Form.Label>
        <Form.Control
          type="text"
          value={detailsInput}
          onChange={e => setDetailsInput(e.target.value)}
          placeholder="Search details..."
        />
      </Form.Group>

      <Button
        variant="outline-danger"
        className="mx-2 mt-4"
        onClick={() => {
          setStartDate('');
          setEndDate('');
          setSelectedCategories([]);
          setDetailsInput('');
        }}
      >
        Clear Filters
      </Button>
    </Form>

    {/* SORTING CONTROLS */}
    <div className="d-flex justify-content-center mb-3">

      {/* Sort by Date */}
      <Dropdown className="mx-2">
        <Dropdown.Toggle variant="dark">
          Sort by Date
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item
            onClick={() => {
              setSortMode('date');
              setSortConfig({ key: 'date', direction: 'desc' });
            }}
          >
            Newest → Oldest
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              setSortMode('date');
              setSortConfig({ key: 'date', direction: 'asc' });
            }}
          >
            Oldest → Newest
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>

      {/* Sort Mode */}
      <Dropdown className="mx-2">
        <Dropdown.Toggle variant="secondary">
          Sort Mode
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item onClick={() => setSortMode('month')}>
            Sort by Month
          </Dropdown.Item>
          <Dropdown.Item onClick={() => setSortMode('category')}>
            Sort by Category
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>

    </div>

    {/* DAILY TOTALS */}
    <h4 className="mt-4">Daily Totals</h4>
    <Table bordered hover responsive className="mb-4">
      <thead>
        <tr>
          <th>Date</th>
          <th>Total Amount</th>
        </tr>
      </thead>
      <tbody>
        {dailyTotals.map(d => (
          <tr key={d.date}>
            <td>{new Date(d.date).toLocaleDateString()}</td>
            <td>D{d.total.toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </Table>

    {/* DAILY CATEGORY TOTALS */}
    <h4 className="mt-4">Daily Category Totals</h4>
    <Table bordered hover responsive className="mb-4">
      <thead>
        <tr>
          <th>Date</th>
          <th>Category</th>
          <th>Total Amount</th>
        </tr>
      </thead>
      <tbody>
        {dailyCategoryTotals.map(day =>
          day.categories.map((c, idx) => (
            <tr key={`${day.date}-${c.cat}`}>
              <td>{idx === 0 ? new Date(day.date).toLocaleDateString() : ''}</td>
              <td>{c.cat}</td>
              <td>D{c.total.toFixed(2)}</td>
            </tr>
          ))
        )}
      </tbody>
    </Table>

    {/* MONTHLY TOTALS */}
    <h4 className="mt-4">Monthly Totals</h4>
    <Table bordered hover responsive className="mb-4">
      <thead>
        <tr>
          <th>Month</th>
          <th>Total Amount</th>
        </tr>
      </thead>
      <tbody>
        {monthlyTotals.map(m => (
          <tr key={m.month}>
            <td>{m.month}</td>
            <td>D{m.total.toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </Table>

    {/* CATEGORY TOTALS */}
    <h4 className="mt-4">Category Totals</h4>
    <Table bordered hover responsive className="mb-4">
      <thead>
        <tr>
          <th>Category</th>
          <th>Total Amount</th>
        </tr>
      </thead>
      <tbody>
        {categoryTotals.map(c => (
          <tr key={c.cat}>
            <td>{c.cat}</td>
            <td>D{c.total.toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </Table>

    {/* MAIN GROUPED TABLE */}
    <Table striped bordered hover responsive>
      <thead className="text-center">
        <tr>
          <th onClick={() => requestSort('note')}>Note</th>
          <th onClick={() => requestSort('details')}>Details</th>
          <th>
            Date
            {sortConfig.key === 'date' &&
              (sortConfig.direction === 'asc' ? ' ▲' : ' ▼')}
          </th>
          <th onClick={() => requestSort('total')}>Total</th>
        </tr>
      </thead>

      <tbody>
        {paginatedCategories.map((item, idx) => {
          if (item.type === 'category') {
            return (
              <tr key={idx} className="table-primary fw-bold">
                <td colSpan={4}>{item.cat}</td>
              </tr>
            );
          }

          if (item.type === 'subtotal') {
            return (
              <tr key={idx} className="table-secondary fw-bold">
                <td colSpan={3} className="text-end">Subtotal:</td>
                <td>{item.total.toFixed(2)}</td>
              </tr>
            );
          }

          return (
            <tr key={idx}>
              <td>{item.note}</td>
              <td>{item.details}</td>
              <td>
                {item.date
                  ? new Date(item.date).toLocaleDateString('en-US')
                  : '-'}
              </td>
              <td>{item.total.toFixed(2)}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>

    {/* FOOTER BUTTONS */}
    <div className="d-flex justify-content-between align-items-center mt-3">

      <Form.Select
        style={{ width: '100px' }}
        value={pageSize}
        onChange={e => setPageSize(Number(e.target.value))}
      >
        {[10, 20, 50, 100].map(size => (
          <option key={size} value={size}>{size}</option>
        ))}
      </Form.Select>

      <div>
        <Button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(p => p - 1)}
          className="mx-1"
        >
          Prev
        </Button>

        <span>Page {currentPage} of {totalPages}</span>

        <Button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(p => p + 1)}
          className="mx-1"
        >
          Next
        </Button>
      </div>

      <Button variant="info" onClick={printDailySummary}>
        Print Daily Summary
      </Button>

      <Button variant="warning" onClick={exportDailySummary}>
        Export Daily Summary
      </Button>

      <Button variant="success" onClick={handleExport}>
        Export Full Summary
      </Button>

      <Button variant="primary" onClick={handlePrint}>
        Print Full Summary
      </Button>

      <Button variant="secondary" onClick={printDailyCategoryTotals}>
        Print Daily Category Totals
      </Button>

      <Button variant="dark" onClick={exportDailyCategoryTotals}>
        Export Daily Category Totals
      </Button>

    </div>
  </div>
);

};

export default OtherExpenseInvoices;